import { ThemeEvents } from '@theme/events';

/**
 * Product subscription chooser - handles dog size, pouch quantity, subscription toggle,
 * and frequency selection. Updates form inputs for quantity and selling_plan.
 */
class ProductSubscriptionChooser {
  /**
   * @param {HTMLElement} root - The subscription-chooser container
   */
  constructor(root) {
    /** @type {HTMLElement} */
    this.root = root;
    /** @type {HTMLInputElement|null} */
    this.quantityInput = root.querySelector('[data-quantity-input]');
    /** @type {HTMLInputElement|null} */
    this.sellingPlanInput = root.querySelector('[data-selling-plan-input]');
    /** @type {HTMLSelectElement|null} */
    this.frequencySelect = root.querySelector('.subscription-chooser__frequency-select');
    /** @type {HTMLElement|null} */
    this.subscriptionContent = root.querySelector('[data-subscription-content]');
    /** @type {HTMLInputElement|null} */
    this.dogSizeLabelPropertyInput = root.querySelector('[data-dog-size-label-property]');
    /** @type {HTMLInputElement|null} */
    this.dogSizeCodePropertyInput = root.querySelector('[data-dog-size-code-property]');

    this.dogSizeMultiplier = 1;
    this.pouchQuantity = 1;
    /** @type {boolean} */
    this.bundleMode = root.dataset.bundleMode === 'true';

    this.#bindEvents();
    this.#updateQuantity();
    this.#updateSubscriptionState();
    this.#updateFrequencyBySelection();
    this.#syncDogSizeLineItemProperties();

    // Re-sync the add-to-cart button price after initial render: custom-element
    // upgrades / section morphs can reset the button text back to the Liquid default.
    requestAnimationFrame(() => this.#updateAddToCartButtonPrice());
    setTimeout(() => this.#updateAddToCartButtonPrice(), 600);
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => this.#updateAddToCartButtonPrice(), { once: true });
    }
    const atcSection = this.root.closest('.shopify-section');
    const atcWrapper = atcSection && atcSection.querySelector('[data-show-price-on-button]');
    if (atcWrapper) {
      new MutationObserver(() => {
        const span = atcWrapper.querySelector('.add-to-cart-text__content span span');
        if (span && !span.textContent.includes(' - ')) this.#updateAddToCartButtonPrice();
      }).observe(atcWrapper, { childList: true, subtree: true, characterData: true });
    }
  }

  /**
   * Bundle: keep the visible quantity stepper in sync with the chooser hidden input (1 pack).
   * Otherwise variant min qty (e.g. 2) can win in FormData / cart.
   */
  #syncBundleQuantitySelectorWithChooser() {
    if (!this.bundleMode || !this.quantityInput) return;
    const form = this.quantityInput.form;
    if (!form) return;
    const qs = /** @type {{ setValue?: (n: number) => void } | null} */ (
      form.querySelector('quantity-selector-component')
    );
    const n = Math.max(1, Number(this.quantityInput.value) || 1);
    qs?.setValue?.(n);
  }

  /**
   * Live checkbox from the DOM (re-query so state stays correct after morph / DOM swaps).
   * @returns {HTMLInputElement|null}
   */
  #getSubscriptionToggle() {
    return /** @type {HTMLInputElement|null} */ (this.root.querySelector('.subscription-chooser__toggle-checkbox'));
  }

  /**
   * Subscription pricing + save badges only when the Subscribe toggle exists and is checked.
   * If there is no toggle (subscription block hidden), treat as one-time for display.
   * @returns {boolean}
   */
  #isSubscriptionActive() {
    const toggle = this.#getSubscriptionToggle();
    if (!toggle) return false;
    return toggle.checked;
  }

  #bindEvents() {
    // Dog size selection - auto-selects pouch quantity: small=1, medium=2, large=3
    this.root.querySelectorAll('.subscription-chooser__dog-size').forEach((/** @type {HTMLElement} */ label) => {
      const input = label.querySelector('input');
      if (!input) return;
      input.addEventListener('change', () => {
        this.dogSizeMultiplier = Number(input.dataset.quantityMultiplier) || 1;
        this.#updateLabel(this.root.querySelector('[data-type="dog_size"]'), this.#getDogSizeLabel(input.value));
        this.#syncDogSizeLineItemProperties(input.value);
        this.root.querySelectorAll('.subscription-chooser__dog-size').forEach((l) => l.classList.remove('subscription-chooser__dog-size--active'));
        label.classList.add('subscription-chooser__dog-size--active');

        // Auto-select pouch quantity based on dog size (bundle: always one line item)
        const pouchForSize = this.bundleMode ? 1 : Number(label.dataset.pouchQuantity) || 1;
        this.pouchQuantity = pouchForSize;
        const pouchOption = /** @type {HTMLElement|null} */ (this.root.querySelector(`.subscription-chooser__quantity-option[data-quantity="${pouchForSize}"]`));
        if (pouchOption) {
          this.#setActiveQuantityOption(pouchOption);
          this.#updateQuantityLabel(pouchOption);
          this.#updatePriceDisplay();
        }
        this.#updateQuantity();
        this.#updateFrequencyBySelection();
      });
    });

    // Pouch quantity selection
    this.root.querySelectorAll('.subscription-chooser__quantity-option').forEach((/** @type {HTMLElement} */ option) => {
      option.addEventListener('click', (e) => {
        if (e.target instanceof Element && e.target.closest('a')) return;
        this.pouchQuantity = Number(option.dataset.quantity) || 1;
        this.#setActiveQuantityOption(option);
        this.#updateQuantityLabel(option);
        this.#updatePriceDisplay();
        this.#updateQuantity();
        this.#updateFrequencyBySelection();
      });
    });

    // Subscription toggle — delegate so updates still work if the checkbox is replaced (e.g. morph)
    this.root.addEventListener('change', (e) => {
      const t = /** @type {HTMLElement} */ (e.target);
      if (t.matches?.('.subscription-chooser__toggle-checkbox')) this.#updateSubscriptionState();
    });
    this.root.addEventListener('input', (e) => {
      const t = /** @type {HTMLElement} */ (e.target);
      if (t.matches?.('.subscription-chooser__toggle-checkbox')) this.#updateSubscriptionState();
    });

    // Frequency select
    if (this.frequencySelect) {
      this.frequencySelect.addEventListener('change', () => {
        this.#updateSellingPlanInput();
      });
    }

    // Variant update - sync add-to-cart price after product-form morphs (e.g. when subscription chooser not present)
    const section = this.root.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, () => this.#updateAddToCartButtonPrice());
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #getDogSizeLabel(value) {
    const labels = /** @type {Record<string, string>} */ ({ small: 'Up to 10kg', medium: '10–25kg', large: '25kg+' });
    return labels[value] || value;
  }

  /**
   * Syncs cart line item properties for reporting (human label + stable code: small|medium|large).
   * @param {string} [sizeValue] - Radio value; if omitted, uses the checked dog-size input.
   */
  #syncDogSizeLineItemProperties(sizeValue) {
    const code =
      sizeValue ||
      /** @type {HTMLInputElement|null} */ (this.root.querySelector('.subscription-chooser__dog-size input:checked'))?.value ||
      'small';
    if (this.dogSizeLabelPropertyInput) {
      this.dogSizeLabelPropertyInput.value = this.#getDogSizeLabel(code);
    }
    if (this.dogSizeCodePropertyInput) {
      this.dogSizeCodePropertyInput.value = code;
    }
  }

  /**
   * @param {HTMLElement|null} element
   * @param {string} text
   */
  #updateLabel(element, text) {
    if (element) element.textContent = text;
  }

  /**
   * @param {Element} activeOption
   */
  #setActiveQuantityOption(activeOption) {
    this.root.querySelectorAll('.subscription-chooser__quantity-option').forEach((opt) => {
      opt.classList.toggle('subscription-chooser__quantity-option--active', opt === activeOption);
    });
  }

  /**
   * @param {HTMLElement} option
   */
  #updateQuantityLabel(option) {
    const labelEl = this.root.querySelector('[data-type="quantity"]');
    if (labelEl) {
      const bundleLabel = option.dataset?.bundleDisplayLabel?.trim();
      if (this.bundleMode && bundleLabel) {
        labelEl.textContent = bundleLabel;
        return;
      }
      const qty = option.dataset?.quantity || '1';
      labelEl.textContent = `${qty} Pouch${qty !== '1' ? 'es' : ''}`;
    }
  }

  /**
   * Per-pouch display from line total (pence), matching Liquid: (line*2+qty)/(2*qty) rounded down for positive amounts.
   * @param {number} linePence
   * @param {number} qty
   * @returns {number}
   */
  #unitPenceFromLine(linePence, qty) {
    const q = Math.max(1, qty);
    return Math.floor((linePence * 2 + q) / (2 * q));
  }

  #updatePriceDisplay() {
    const isSubscription = this.#isSubscriptionActive();

    this.root.querySelectorAll('.subscription-chooser__quantity-option').forEach((/** @type {HTMLElement} */ opt) => {
      const priceEl = opt.querySelector('[data-price]');
      const servingEl = opt.querySelector('[data-per-serving]');
      const wasPriceEl = /** @type {HTMLElement|null} */ (opt.querySelector('[data-was-price]'));
      const saveBadgeEl = /** @type {HTMLElement|null} */ (opt.querySelector('[data-save-badge]'));
      const shippingEl = opt.querySelector('[data-shipping]');
      const giftEl = /** @type {HTMLElement|null} */ (opt.querySelector('[data-gift]'));

      const pKey = isSubscription ? 'priceSub' : 'priceOnetime';
      const sKey = isSubscription ? 'perServingSub' : 'perServingOnetime';
      const shipKey = isSubscription ? 'shippingSub' : 'shippingOnetime';
      const giftKey = isSubscription ? 'giftSub' : 'giftOnetime';

      const qty = Number(opt.dataset.quantity) || 1;
      const linePence = Number(opt.dataset?.[pKey] || 0);
      const unitPence = this.#unitPenceFromLine(linePence, qty);
      const unitRrpPence = Number(opt.dataset?.unitRrp || 0);
      const savePercentSub = Number(opt.getAttribute('data-save-percent-sub') || 0);
      const savePercentOnetime = Number(opt.getAttribute('data-save-percent-onetime') || 0);
      const shipPence = Number(opt.dataset?.[shipKey] || 0);
      const giftText = opt.dataset?.[giftKey] || '';

      if (priceEl) {
        priceEl.textContent = this.#formatPrice(unitPence);
      }

      if (wasPriceEl) {
        if (unitRrpPence > unitPence) {
          wasPriceEl.textContent = this.#formatPrice(unitRrpPence);
          wasPriceEl.hidden = false;
        } else {
          wasPriceEl.hidden = true;
        }
      }

      if (saveBadgeEl) {
        const showSave =
          (isSubscription && savePercentSub > 0) || (!isSubscription && savePercentOnetime > 0);
        if (showSave) {
          const pct = isSubscription ? savePercentSub : savePercentOnetime;
          saveBadgeEl.textContent = `Save ${pct}%`;
          saveBadgeEl.hidden = false;
        } else {
          saveBadgeEl.hidden = true;
        }
      }

      if (servingEl) {
        const raw = String(opt.dataset?.[sKey] ?? '0').trim();
        const n = Number.parseFloat(raw);
        const formatted = Number.isFinite(n) ? n.toFixed(2) : '0.00';
        servingEl.textContent = `£${formatted} per serving`;
      }

      if (shippingEl) {
        if (shipPence === 0) {
          shippingEl.textContent = 'FREE Delivery';
          shippingEl.classList.add('subscription-chooser__perk--free');
        } else {
          shippingEl.textContent = `Shipping ${this.#formatPrice(shipPence)}`;
          shippingEl.classList.remove('subscription-chooser__perk--free');
        }
      }

      if (giftEl) {
        const showGift = Boolean(giftText);
        giftEl.hidden = !showGift;
        giftEl.style.display = showGift ? '' : 'none';
      }
    });

    this.#updateAddToCartButtonPrice();
  }

  /**
   * Updates the add-to-cart button price when show_price_on_button is enabled.
   * Syncs with subscription chooser's active quantity and subscription state.
   */
  #updateAddToCartButtonPrice() {
    const section = this.root.closest('.shopify-section');
    if (!section) return;

    const addToCartWrapper = section.querySelector('[data-show-price-on-button]');
    if (!addToCartWrapper) return;

    const activeOption = /** @type {HTMLElement | null} */ (
      this.root.querySelector('.subscription-chooser__quantity-option--active')
    );
    if (!activeOption) return;

    const isSub = this.#isSubscriptionActive();
    const lineKey = isSub ? 'priceSub' : 'priceOnetime';
    const linePence = Number(activeOption.dataset[lineKey] || 0);
    if (!Number.isFinite(linePence)) return;

    // Strikethrough anchor: product RRP; subscription also includes free gift + express shipping RRP
    const rrpKey = isSub ? 'rrpSub' : 'rrpOnetime';
    let strikePence = Number(activeOption.dataset[rrpKey] || 0);
    if (!Number.isFinite(strikePence)) strikePence = 0;
    if (strikePence > 0 && isSub) {
      const rootData = this.root.dataset;
      const giftIncluded = (activeOption.dataset.giftSub || '') !== '' && Number(rootData.giftPrice || 0) > 0;
      if (giftIncluded) strikePence += Number(rootData.giftPrice || 0);
      strikePence += Number(rootData.shippingRrp || 0);
    }

    const baseText = /** @type {HTMLElement} */ (addToCartWrapper).dataset.addToCartBaseText || 'Add to cart';
    const price = this.#formatPrice(linePence);
    const addToCartComponent = addToCartWrapper.querySelector('add-to-cart-component');
    const button =
      /** @type {{ refs?: { addToCartButton?: HTMLButtonElement } } | null} */ (addToCartComponent)?.refs
        ?.addToCartButton ?? addToCartWrapper.querySelector('[ref="addToCartButton"]');
    const textSpan = button?.querySelector('.add-to-cart-text__content span span');

    if (textSpan && price) {
      if (strikePence > linePence) {
        textSpan.textContent = '';
        const strike = document.createElement('s');
        strike.className = 'add-to-cart-strike';
        strike.textContent = this.#formatPrice(strikePence);
        textSpan.append(`${baseText} - `, strike, ` ${price}`);
      } else {
        textSpan.textContent = `${baseText} - ${price}`;
      }
    }
  }

  /**
   * @param {number} pence
   * @returns {string}
   */
  #formatPrice(pence) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
    }).format(pence / 100);
  }

  #updateQuantity() {
    if (this.quantityInput) {
      this.quantityInput.value = String(Math.max(1, this.pouchQuantity));
    }
    if (this.bundleMode) {
      this.#syncBundleQuantitySelectorWithChooser();
    }
  }

  #updateSubscriptionState() {
    const toggle = this.#getSubscriptionToggle();
    const isChecked = toggle ? toggle.checked : false;

    if (this.subscriptionContent) {
      this.subscriptionContent.hidden = !isChecked;
    }

    if (this.sellingPlanInput) {
      if (isChecked) {
        this.sellingPlanInput.removeAttribute('disabled');
        this.sellingPlanInput.name = 'selling_plan';
        this.#updateSellingPlanInput();
      } else {
        this.sellingPlanInput.setAttribute('disabled', 'disabled');
        this.sellingPlanInput.removeAttribute('name');
      }
    }

    // Update price display when toggling subscription
    this.#updatePriceDisplay();
  }

  #updateSellingPlanInput() {
    if (this.sellingPlanInput && this.frequencySelect) {
      this.sellingPlanInput.value = this.frequencySelect.value;
    }
  }

  /**
   * Bundle mode: map dog size (small|medium|large) to the selling plan option tagged with data-bundle-dog-size.
   * @param {string} dogSizeCode
   */
  #syncBundleFrequencyToDogSize(dogSizeCode) {
    if (!this.frequencySelect) return;
    const opt = /** @type {HTMLOptionElement | null} */ (
      this.frequencySelect.querySelector(`option[data-bundle-dog-size="${dogSizeCode}"]`)
    );
    if (opt) {
      opt.selected = true;
      this.#updateSellingPlanInput();
      return;
    }
    if (this.frequencySelect.options.length > 0) {
      this.frequencySelect.selectedIndex = 0;
      this.#updateSellingPlanInput();
    }
  }

  #updateFrequencyBySelection() {
    if (!this.frequencySelect) return;
    if (this.bundleMode) {
      const code =
        /** @type {HTMLInputElement | null} */ (this.root.querySelector('.subscription-chooser__dog-size input:checked'))
          ?.value || 'small';
      this.#syncBundleFrequencyToDogSize(code);
      return;
    }
    const option = Array.from(this.frequencySelect.options).find((opt) => {
      if (opt.hasAttribute('data-manual-frequency-option')) return false;
      const raw = opt.getAttribute('data-pouch-quantity');
      if (raw === null || String(raw).trim() === '') return false;
      const n = Number(raw);
      return Number.isFinite(n) && n >= 1 && n === this.pouchQuantity;
    });
    if (option) {
      // Same selling plan id can appear twice (e.g. 1p & 3p on 8-week); set selected option, not .value only.
      option.selected = true;
      this.#updateSellingPlanInput();
    } else if (this.frequencySelect.options.length > 0) {
      // No matching pouch row (e.g. theme data mismatch); keep first option so a plan is always submitted.
      this.frequencySelect.selectedIndex = 0;
      this.#updateSellingPlanInput();
    }
  }
}

/**
 * Initialize subscription choosers on the page.
 */
export function initProductSubscription() {
  document.querySelectorAll('.subscription-chooser').forEach((/** @type {HTMLElement} */ el) => {
    if (!el.dataset.initialized) {
      el.dataset.initialized = 'true';
      new ProductSubscriptionChooser(el);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductSubscription);
} else {
  initProductSubscription();
}

// Re-run when section is re-rendered (e.g. variant change)
document.addEventListener('shopify:section:load', initProductSubscription);
